/** PATRON POINTS — the owner's page: what points are worth, who is earning
 *  them, and every point that has moved.
 *
 *  Layout follows the Review Shield results page (Card + Table + Badge, the
 *  organization chosen at the top), with this module's own vocabulary: points,
 *  vouchers, game night.
 */
import { useCallback, useEffect, useState } from 'react';
import { Coins, Gift, Loader2, Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import PatronPagesBlock from '../../components/patron/PatronPagesBlock';
import RemotePlayBlock from '../../components/patron/RemotePlayBlock';

const KIND_LABEL = {
    attendance: 'Attendance',
    team_goal: 'Team goal',
    star_goal: 'Star scored',
    redemption: 'Reward voucher',
};

const num = (n) => Number(n || 0).toLocaleString();
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined,
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const Field = ({ label, value, onChange, testid, hint }) => (
    <label className="block">
        <span className="block text-xs font-semibold text-slate-500 mb-1">{label}</span>
        <Input type="number" value={value} data-testid={testid}
               onChange={(e) => onChange(e.target.value)} className="tabular-nums" />
        {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
);

export default function PatronPoints() {
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/organizations');
                const rows = (res.data?.organizations || res.data || [])
                    .filter((o) => o.goal_game_enabled);
                setOrgs(rows);
                // A link from the Goal Game page names its venue: open on it.
                const wanted = new URLSearchParams(window.location.search)
                    .get('organization_id');
                if (wanted && rows.some((o) => o.id === wanted)) setOrgId(wanted);
                else if (rows.length) setOrgId(rows[0].id);
            } catch (_e) { toast.error('Could not load the venues'); }
        })();
    }, []);

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/patron/admin/overview', { params: { organization_id: orgId } });
            setData(res.data);
            setDraft(res.data.settings);
        } catch (_e) { toast.error('Could not load Patriot Points'); }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const save = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/patron/admin/settings', { organization_id: orgId, ...patch });
            setDraft(res.data.settings);
            await load();
            toast.success('Saved');
        } catch (_e) { toast.error('Could not save'); }
        finally { setBusy(false); }
    };

    if (!draft || !data) {
        return <div className="p-8 text-sm text-slate-500" data-testid="patron-admin-loading">…</div>;
    }

    const t = data.totals;

    return (
        <div className="p-4 sm:p-6 space-y-6" data-testid="patron-admin">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold grow" data-testid="patron-admin-title">
                    <Coins size={20} className="inline mr-2 -mt-1" />Patriot Points
                </h1>
                <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                        data-testid="patron-admin-org"
                        className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                    {orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.display_name || o.name}</option>
                    ))}
                </select>
                <Badge variant={draft.enabled ? 'default' : 'secondary'} data-testid="patron-admin-state">
                    {draft.enabled ? 'On' : 'Off'}
                </Badge>
                <Button variant={draft.enabled ? 'outline' : 'default'} disabled={busy}
                        data-testid="patron-admin-toggle"
                        onClick={() => save({ enabled: !draft.enabled })}>
                    {draft.enabled ? 'Turn off' : 'Turn on'}
                </Button>
            </div>

            <div className="grid sm:grid-cols-4 gap-3">
                {[['Fans', t.fans, Users], ['Points awarded', num(t.points_awarded), Coins],
                  ['Points spent', num(t.points_spent), Coins],
                  ['Reward vouchers', t.vouchers, Gift]].map(([label, value, Icon]) => (
                    <Card key={label}>
                        <CardContent className="pt-5">
                            <p className="text-xs uppercase tracking-wider text-slate-500">
                                <Icon size={12} className="inline mr-1 -mt-0.5" />{label}
                            </p>
                            <p className="text-2xl font-bold tabular-nums" data-testid={`patron-total-${label.toLowerCase().replace(/ /g, '-')}`}>
                                {value}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Card>
                <CardHeader><CardTitle className="text-base">What points are worth</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="Attendance" value={draft.points_attendance}
                               testid="patron-points-attendance"
                               hint="Once per fan per game night"
                               onChange={(v) => setDraft({ ...draft, points_attendance: v })} />
                        <Field label="Team goal" value={draft.points_team_goal}
                               testid="patron-points-team-goal"
                               hint="Every goal that opens a round"
                               onChange={(v) => setDraft({ ...draft, points_team_goal: v })} />
                        <Field label="Star of the game scores" value={draft.points_star_goal}
                               testid="patron-points-star-goal"
                               hint="Only goals scored after the fan's pick"
                               onChange={(v) => setDraft({ ...draft, points_star_goal: v })} />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <label className="block">
                            <span className="block text-xs font-semibold text-slate-500 mb-1">Reward wording</span>
                            <Input value={draft.reward_label} data-testid="patron-reward-label"
                                   onChange={(e) => setDraft({ ...draft, reward_label: e.target.value })} />
                        </label>
                        <Field label="Reward costs" value={draft.reward_cost}
                               testid="patron-reward-cost" hint="Points spent per voucher"
                               onChange={(v) => setDraft({ ...draft, reward_cost: v })} />
                        <Field label="Voucher valid for (days)" value={draft.reward_expiry_days}
                               testid="patron-reward-days"
                               hint="0 = this game night only"
                               onChange={(v) => setDraft({ ...draft, reward_expiry_days: v })} />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="Players per table" value={draft.max_players_per_table}
                               testid="patron-max-players"
                               hint="The table's owner counts as player 1"
                               onChange={(v) => setDraft({ ...draft, max_players_per_table: v })} />
                        <label className="block">
                            <span className="block text-xs font-semibold text-slate-500 mb-1">Demo login email</span>
                            <Input value={draft.demo_login_email || ''} data-testid="patron-demo-login-email"
                                   placeholder="empty = off"
                                   onChange={(e) => setDraft({ ...draft, demo_login_email: e.target.value })} />
                            <span className="block text-xs text-slate-400 mt-1">
                                Demo organizations only. This one email signs in with any 4-digit
                                code and shows a fixed balance that cannot be redeemed. Empty = off.
                            </span>
                        </label>
                        <label className="block">
                            <span className="block text-xs font-semibold text-slate-500 mb-1">Demo login screen name</span>
                            <Input value={draft.demo_login_name || ''} data-testid="patron-demo-login-name"
                                   placeholder="e.g. Jay"
                                   onChange={(e) => setDraft({ ...draft, demo_login_name: e.target.value })} />
                            <span className="block text-xs text-slate-400 mt-1">
                                Shown as "Welcome back, ...". Empty uses the email name.
                            </span>
                        </label>
                    </div>
                    <Button disabled={busy} data-testid="patron-save"
                            onClick={() => save({
                                points_attendance: Number(draft.points_attendance),
                                points_team_goal: Number(draft.points_team_goal),
                                points_star_goal: Number(draft.points_star_goal),
                                reward_label: draft.reward_label,
                                reward_cost: Number(draft.reward_cost),
                                reward_expiry_days: Number(draft.reward_expiry_days),
                                max_players_per_table: Number(draft.max_players_per_table),
                                demo_login_email: draft.demo_login_email || '',
                                demo_login_name: draft.demo_login_name || '',
                            })}>
                        {busy ? <Loader2 className="animate-spin mr-2" size={15} />
                              : <Save className="mr-2" size={15} />}
                        Save
                    </Button>
                    <p className="text-xs text-slate-500">
                        A fan's balance is the sum of its ledger rows — there is no editable balance.
                        The same goal can never be credited twice: the database refuses it.
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Play from anywhere</CardTitle>
                </CardHeader>
                <CardContent>
                    <RemotePlayBlock orgId={orgId} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Fan page — how points work</CardTitle>
                </CardHeader>
                <CardContent>
                    <PatronPagesBlock orgId={orgId} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Fans ({data.fans.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    {data.fans.length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="patron-fans-empty">
                            No fan has kept points yet.
                        </p>
                    ) : (
                        <Table data-testid="patron-fans-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead className="text-right">Points</TableHead>
                                    <TableHead>Consent given</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.fans.map((f) => (
                                    <TableRow key={f.id} data-testid={`patron-fan-${f.id}`}>
                                        <TableCell className="font-medium">{f.email}</TableCell>
                                        <TableCell className="text-right tabular-nums">{num(f.points)}</TableCell>
                                        <TableCell className="text-xs text-slate-500">{when(f.consent_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Ledger — game night {data.game_night}</CardTitle>
                </CardHeader>
                <CardContent>
                    {data.ledger.length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="patron-ledger-empty">
                            Nothing has moved yet.
                        </p>
                    ) : (
                        <Table data-testid="patron-ledger-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>When</TableHead>
                                    <TableHead>Fan</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead className="text-right">Points</TableHead>
                                    <TableHead>Voucher</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.ledger.map((r) => (
                                    <TableRow key={r.id} data-testid={`patron-ledger-${r.id}`}>
                                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{when(r.created_at)}</TableCell>
                                        <TableCell className="text-xs">{r.email}</TableCell>
                                        <TableCell>{KIND_LABEL[r.kind] || r.kind}{r.note ? ` — ${r.note}` : ''}</TableCell>
                                        <TableCell className={`text-right tabular-nums font-semibold ${r.points < 0 ? 'text-amber-600' : ''}`}>
                                            {r.points > 0 ? '+' : ''}{num(r.points)}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">{r.voucher_code || ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
